"use client";

import { Button } from "@universe/ui/components/button";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  cleanUpMockSession,
  clearOidcSession,
  enableMockMode,
  isBuildTimeMockMode,
  isMockModeActive,
  isMockServiceWorkerUrl,
  isRuntimeMockModeAllowed,
  MOCK_MODE_QUERY_PARAM,
  MOCK_MODE_STORAGE_KEY,
  MOCK_REACTIVATE_EVENT,
  MOCK_SERVICE_WORKER_URL,
  readMockModeParam,
  setMockModeDecision,
} from "@/lib/mockMode";
import { removeLoader } from "@/splashScreen";
import { runWithCleanup } from "@/utils/runWithCleanup";

import { onUnhandledRequest } from "./unhandledRequest";

type Status = "deciding" | "real" | "mock" | "failed";
export type MockModeFailureReason =
  | "insecure"
  | "start-failed"
  | "no-controller"
  | "error";
type MockModeContextValue = { isMockMode: boolean };

const REAL_MODE: MockModeContextValue = { isMockMode: false };
const MOCK_MODE: MockModeContextValue = { isMockMode: true };
const MockModeContext = createContext<MockModeContextValue>(REAL_MODE);

/** `{ isMockMode: false }` outside the gate (Storybook, unit tests). */
export const useMockMode = () => useContext(MockModeContext);

// One worker per page: StrictMode runs the effect twice in `next dev`, and two
// started workers would both answer every request.
let workerStart: Promise<void> | undefined;

const startWorker = (): Promise<void> => {
  workerStart ??= (async () => {
    const [{ setupWorker }, { handlers }] = await Promise.all([
      import("msw/browser"),
      import("@/mocks/handlers"),
    ]);
    await setupWorker(...handlers).start({
      serviceWorker: { url: MOCK_SERVICE_WORKER_URL },
      onUnhandledRequest,
      quiet: true,
    });
  })().catch((error: unknown) => {
    workerStart = undefined; // a failed start must not be cached as the answer
    throw error;
  });
  return workerStart;
};

/** Test-only: forget the module-level worker start between specs. */
export const resetMockWorkerForTests = () => {
  workerStart = undefined;
};

const CONTROLLER_WAIT_MS = 3000;
let controllerWaitMs = CONTROLLER_WAIT_MS;

/** Test-only: shorten the controller wait; `undefined` restores the default. */
export const setControllerWaitMsForTests = (ms: number | undefined) => {
  controllerWaitMs = ms ?? CONTROLLER_WAIT_MS;
};

// How long to wait, beyond the controller wait, for MSW's own reload to land
// before giving up on it.
const RELOAD_WAIT_MS = 5000;
let reloadWaitMs = RELOAD_WAIT_MS;

/** Test-only: shorten the reload wait; `undefined` restores the default. */
export const setReloadWaitMsForTests = (ms: number | undefined) => {
  reloadWaitMs = ms ?? RELOAD_WAIT_MS;
};

/**
 * MSW's own reload condition (msw/lib/browser getWorkerInstance): no
 * controller while a registration holds MSW's worker — matched the way MSW
 * matches it (`getWorkerByRegistration`: active, installing or waiting whose
 * `scriptURL` is exactly the absolute worker URL). A hard reload
 * (Shift+Cmd+R) bypasses the worker for the whole page; MSW then calls
 * `location.reload()` but its `start()` can still resolve.
 */
const isWorkerReloadExpected = async (sw: ServiceWorkerContainer) => {
  if (sw.controller) return false;
  const registrations = await sw.getRegistrations();
  return registrations.some((r) =>
    [r.active, r.installing, r.waiting].some((worker) =>
      isMockServiceWorkerUrl(worker?.scriptURL)
    )
  );
};

/**
 * Whether `?mockMode=false` has anything to clean: the flag is on, or an MSW
 * registration is left on this origin (a hard-killed `dev:mock`).
 */
const hasMockSessionToClean = async (): Promise<boolean> => {
  if (isMockModeActive()) return true;
  if (!("serviceWorker" in navigator)) return false;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    return registrations.some((r) =>
      [r.active, r.installing, r.waiting].some((worker) =>
        worker?.scriptURL.includes("mockServiceWorker")
      )
    );
  } catch {
    // Can't tell: clean up anyway (fail towards leaving mock mode).
    return true;
  }
};

/**
 * Resolves true once a service worker controls this page. Requests from an
 * uncontrolled page bypass MSW entirely, so "started" is not enough: on a
 * first install the controller arrives via the worker's `clients.claim()`,
 * possibly after `start()` resolves.
 */
const waitForController = (
  sw: ServiceWorkerContainer,
  waitMs: number = controllerWaitMs
): Promise<boolean> => {
  if (sw.controller) return Promise.resolve(true);
  return new Promise((resolve) => {
    const done = (controlled: boolean) => {
      clearTimeout(timer);
      sw.removeEventListener("controllerchange", onChange);
      resolve(controlled);
    };
    const onChange = () => {
      if (sw.controller) done(true);
    };
    const timer = setTimeout(() => done(Boolean(sw.controller)), waitMs);
    sw.addEventListener("controllerchange", onChange);
  });
};

/**
 * Re-adds this page to the worker's `activeClientIds` — the same
 * `MOCK_ACTIVATE` message MSW's `start()` sends (msw/lib/browser
 * `enableMocking`), awaiting the worker's `MOCKING_ENABLED` reply.
 *
 * Why not `worker.start()` again: it is a no-op while MSW's page-side
 * `isMockingEnabled` is true (it warns and returns). `stop()` + `start()`
 * does re-send it, but opens a window where MSW passes requests through,
 * re-runs registration (which may `location.reload()`), and still cannot
 * rebind to an updated worker: MSW's channel is fixed to the worker it first
 * resolved. Posting the message directly has none of those costs.
 */
const activateClient = (
  sw: ServiceWorkerContainer,
  controller: ServiceWorker
): Promise<boolean> =>
  new Promise((resolve) => {
    const done = (activated: boolean) => {
      clearTimeout(timer);
      sw.removeEventListener("message", onMessage);
      resolve(activated);
    };
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown } | null;
      if (event.source === controller && data?.type === "MOCKING_ENABLED")
        done(true);
    };
    const timer = setTimeout(() => done(false), controllerWaitMs);
    sw.addEventListener("message", onMessage);
    controller.postMessage("MOCK_ACTIVATE");
  });

type Reassertion = "active" | "reload" | "no-controller" | "unacknowledged";

/**
 * A worker can lose this page between requests: the browser terminates an
 * idle worker (~30s; the keepalive is throttled in hidden tabs) and restarts
 * it with an empty `activeClientIds`, or a worker update takes control. Both
 * make every request pass through to the network. Re-assert mocking against
 * the current controller: same worker → re-activate; an updated MSW worker →
 * reload (MSW's page side only talks to the worker it started with);
 * anything else → fail closed.
 */
const reassertMocking = async (
  sw: ServiceWorkerContainer,
  boundWorker: ServiceWorker | null
): Promise<Reassertion> => {
  const controller = sw.controller;
  if (!controller || !isMockServiceWorkerUrl(controller.scriptURL))
    return "no-controller";
  if (boundWorker && controller !== boundWorker) return "reload";
  return (await activateClient(sw, controller)) ? "active" : "unacknowledged";
};

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

// Plain console, not logError: mock mode must not emit telemetry.
const logCleanupFailure = (error: unknown) => {
  console.error(
    `[mock-mode] Mock session cleanup failed: ${errorMessage(error)}`
  );
};

const FAILURE_COPY: Record<MockModeFailureReason, string> = {
  insecure:
    "Service workers are unavailable here — most likely an insecure context. Open the app over https:// or on localhost.",
  "start-failed":
    "The mock service worker failed to start. Check the browser console for the error, then try again.",
  "no-controller":
    "The mock service worker isn't controlling this page — a hard reload bypasses it, or it was stopped or replaced. Try again to reload normally.",
  error:
    "Something went wrong while running mock mode. Check the browser console, then try again.",
};

function MockModeFailed({ reason }: { reason: MockModeFailureReason }) {
  const [exiting, setExiting] = useState(false);

  // Nothing under the gate mounts on failure, so nothing else will lift the
  // static #globalLoader veil — without this the card sits hidden under it.
  useEffect(() => {
    removeLoader();
  }, []);

  const exitMockMode = async () => {
    setExiting(true);
    // Always leave: a failed cleanup must not strand the user on this card.
    // The failure is logged inside the task, so it lands before the reload.
    await runWithCleanup(
      () => cleanUpMockSession().catch(logCleanupFailure),
      () => window.location.assign("/")
    );
  };

  return (
    <div
      data-testid="mock-mode-failed"
      data-reason={reason}
      translate="no"
      className="flex min-h-screen items-center justify-center p-6"
    >
      <div className="bg-card flex max-w-md flex-col gap-4 rounded-lg border p-6 shadow-sm">
        <h1 className="text-lg font-semibold">Mock mode could not start</h1>
        <p className="text-muted-foreground text-sm">{FAILURE_COPY[reason]}</p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => window.location.reload()}
          >
            Try again
          </Button>
          {isBuildTimeMockMode() ? (
            // Forced on by the build: there is nothing to exit from here.
            <p className="text-muted-foreground self-center text-sm">
              Restart the dev server without NEXT_PUBLIC_MOCK_MODE
            </p>
          ) : (
            <Button
              type="button"
              disabled={exiting}
              onClick={() => void exitMockMode()}
            >
              {exiting ? "Exiting mock mode…" : "Exit mock mode"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Decides real vs mock mode before anything else mounts. In mock mode the MSW
 * worker must be active before children render — auth and RTK Query fire on
 * mount — and if it cannot start the gate fails closed so the fake token
 * never reaches a real backend.
 */
export function MockModeGate({ children }: WithChildren) {
  // Where mock mode is impossible (prd, no build-time flag) start in "real" so
  // SSR and the first client render ship the page exactly as before the gate.
  // Everywhere else start in "deciding" (render nothing) — fail closed until
  // the effect knows whether a worker must be active first.
  // (The build-time flag is itself refused on prd, so this is the whole test.)
  const [status, setStatus] = useState<Status>(() => {
    if (isRuntimeMockModeAllowed()) return "deciding";
    // Latched during render, not in an effect: children's effects (auth, RTK
    // Query) run before this component's own. Browser only — module state on
    // the server would be shared across requests.
    if (typeof window !== "undefined") setMockModeDecision(false);
    return "real";
  });
  const [failure, setFailure] = useState<MockModeFailureReason>("error");
  const fail = useCallback((reason: MockModeFailureReason) => {
    setFailure(reason);
    setStatus("failed");
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Latch the page's decision (lib/mockMode.ts) before children mount.
    const commit = (decision: "mock" | "real") => {
      setMockModeDecision(decision === "mock");
      setStatus(decision);
    };
    const decideSafely = async () => {
      try {
        await decide();
      } catch (error) {
        // Anything unexpected (blocked storage, a throwing API) fails closed
        // where mock mode could be on, and stays real where it never can.
        // Plain console, not logError: mock mode must not emit telemetry.
        console.error(
          `[mock-mode] Deciding mock vs real mode failed: ${errorMessage(error)}`
        );
        if (cancelled) return;
        if (isRuntimeMockModeAllowed()) fail("error");
        else commit("real");
      }
    };
    const decide = async () => {
      const action = readMockModeParam(window.location.search);
      // `?mockMode=false` on a page with no mock session (no flag, no MSW
      // registration) has nothing to clean: a real user's oidc.*, account and
      // onboarding keys must survive it, so treat it like a disallowed param.
      const needsRedirect =
        isRuntimeMockModeAllowed() &&
        (action === "enable" ||
          (action === "disable" && (await hasMockSessionToClean())));
      if (needsRedirect) {
        if (action === "enable") enableMockMode();
        else {
          try {
            await cleanUpMockSession();
          } catch (error) {
            // Leave anyway, like the dev panel and the Exit button: a failed
            // cleanup must not strand the page. Plain console, not logError:
            // mock mode must not emit telemetry.
            console.error(
              `[mock-mode] Mock session cleanup failed: ${errorMessage(error)}`
            );
          }
        }
        window.location.assign(`${window.location.origin}/`);
        return; // stay "deciding": nothing renders during the redirect
      }
      if (action) {
        // Not allowed on this build, or nothing to clean: drop the param,
        // carry on (the checks below decide real vs mock as usual).
        const url = new URL(window.location.href);
        url.searchParams.delete(MOCK_MODE_QUERY_PARAM);
        window.history.replaceState(window.history.state, "", url);
      }
      if (!isMockModeActive()) {
        if (!cancelled) commit("real");
        return;
      }
      if (!window.isSecureContext || !("serviceWorker" in navigator)) {
        console.error(
          "[mock-mode] Service workers unavailable (insecure context?) — refusing to boot with a mock token."
        );
        if (!cancelled) fail("insecure");
        return;
      }
      try {
        // Checked before start: MSW's own start may reload the page.
        const reloadExpected = await isWorkerReloadExpected(
          navigator.serviceWorker
        );
        await startWorker();
        let controlled = await waitForController(navigator.serviceWorker);
        if (!controlled && reloadExpected) {
          // MSW's own location.reload() should be in flight: stay "deciding"
          // (blank) for it — but only for so long.
          console.warn(
            "[mock-mode] No service worker controls this page yet — waiting for MSW's reload."
          );
          controlled = await waitForController(
            navigator.serviceWorker,
            reloadWaitMs
          );
        }
        if (!controlled) {
          console.error(
            "[mock-mode] MSW started but no service worker controls the page — refusing to boot with a mock token."
          );
          if (!cancelled) fail("no-controller");
          return;
        }
        // Before any child mounts: drop a real session left in storage (also
        // covers the build-time `dev:mock` flag, which never passes through
        // enableMockMode), so baseQuery and the auth layer can't read it.
        clearOidcSession();
        if (!cancelled) commit("mock");
      } catch (error) {
        // Plain console, not logError: mock mode must not emit telemetry.
        console.error(
          `[mock-mode] MSW worker failed to start: ${errorMessage(error)}`
        );
        if (!cancelled) fail("start-failed");
      }
    };
    void decideSafely();
    return () => {
      cancelled = true;
    };
  }, [fail]);

  // Another tab flipping the shared flag must not leave this page running on
  // the other mode's assumptions: reload so the gate decides again. (Requests
  // are safe meanwhile — baseQuery reads this page's latch, not the flag.)
  useEffect(() => {
    if (
      status === "deciding" ||
      !isRuntimeMockModeAllowed() ||
      isBuildTimeMockMode() // forced on: the stored flag is irrelevant
    )
      return;
    const pageIsMock = status !== "real"; // "failed" is a mock page
    const onStorage = (event: StorageEvent) => {
      // `key === null` is a `clear()` from another tab: the flag is gone.
      if (event.key !== null && event.key !== MOCK_MODE_STORAGE_KEY) return;
      const flagOn = event.key !== null && event.newValue === "true";
      if (flagOn === pageIsMock) return;
      console.warn(
        "[mock-mode] Mock mode was switched in another tab — reloading this one."
      );
      window.location.reload();
    };
    // A back/forward-cache restore resumes this page as it was frozen: the
    // flag may have flipped meanwhile (the storage event fired while frozen
    // is not replayed), so compare again and re-decide on a mismatch.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || isMockModeActive() === pageIsMock) return;
      console.warn(
        "[mock-mode] Restored from the back/forward cache in the other mode — reloading."
      );
      window.location.reload();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [status]);

  // While mocking, re-assert it whenever the worker may have lost this page.
  // One re-assertion in flight at a time: a burst of triggers coalesces.
  useEffect(() => {
    if (status !== "mock") return;
    const sw = navigator.serviceWorker;
    // The worker MSW started with; its channel ignores any other.
    const boundWorker = sw.controller;
    let disposed = false;
    let inFlight = false;
    const reassert = () => {
      if (inFlight) return;
      inFlight = true;
      reassertMocking(sw, boundWorker)
        .then((outcome) => {
          if (disposed || outcome === "active") return;
          if (outcome === "reload") {
            console.warn(
              "[mock-mode] An updated mock service worker took control — reloading to rebind MSW."
            );
            setStatus("deciding"); // nothing renders against an unbound worker
            window.location.reload();
            return;
          }
          console.error(
            "[mock-mode] The mock service worker no longer serves this page — refusing to continue with a mock token."
          );
          fail(outcome === "no-controller" ? "no-controller" : "error");
        })
        .catch((error: unknown) => {
          console.error(
            `[mock-mode] Re-activating the mock service worker failed: ${errorMessage(error)}`
          );
          if (!disposed) fail("error");
        })
        .finally(() => {
          inFlight = false;
        });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") reassert();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    sw.addEventListener("controllerchange", reassert);
    // baseQuery saw a mock-mode call get no response at all: the worker lost
    // this page while it stayed visible (requestMockReactivation,
    // lib/mockMode.ts).
    window.addEventListener(MOCK_REACTIVATE_EVENT, reassert);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      sw.removeEventListener("controllerchange", reassert);
      window.removeEventListener(MOCK_REACTIVATE_EVENT, reassert);
    };
  }, [status, fail]);

  const value = useMemo<MockModeContextValue>(
    () => (status === "mock" ? MOCK_MODE : REAL_MODE),
    [status]
  );

  if (status === "deciding") return null;
  if (status === "failed") return <MockModeFailed reason={failure} />;
  return (
    <MockModeContext.Provider value={value}>
      {children}
    </MockModeContext.Provider>
  );
}
